import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useGPSTracking } from '@/hooks/useGPSTracking';
import { getHeatLabel, incrementHeat, peekNextHeat } from '@/hooks/useHeatCounter';
import { saveSessionWithRetry } from '@/lib/sessionSave';
import {
  Play,
  Square,
  Target,
  Timer,
  Gauge,
  MapPin,
  AlertCircle,
  CheckCircle2,
  Zap,
  ArrowLeft,
  Wifi,
  WifiOff,
  Signal,
  Monitor,
  AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// DBPRO Brand Colors
const BRAND = {
  navy: '#0A1A2F',
  navyLight: '#112240',
  navyCard: '#1A2E4A',
  red: '#E53935',
  redHover: '#C62828',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  mutedGray: '#B0BEC5',
  darkGray: '#64748B'
};

// Format duration from seconds to mm:ss
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Format distance
const formatDistance = (meters) => {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
};

// Format speed
const formatSpeed = (ms) => {
  const kmh = ms * 3.6;
  return {
    ms: ms.toFixed(2),
    kmh: kmh.toFixed(1)
  };
};

const DashboardScreen_DBPRO_GPS_v2_BrandStyling = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const autoStartedRef = useRef(false);
  
  // Get session config from navigation state (from Training Dashboard)
  const sessionConfig = location.state || {};
  
  const [mode] = useState(sessionConfig.mode || 'practice');
  const [autoStopEnabled] = useState(sessionConfig.autoStopEnabled || false);
  const [lastSession, setLastSession] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [heatNumber, setHeatNumber] = useState(() => peekNextHeat(sessionConfig.targetDistance || 200));
  const [showSavedPulse, setShowSavedPulse] = useState(false);
  
  // Detect if user is in an in-app browser (Facebook, Messenger, Instagram, etc.)
  const isInAppBrowser = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return /FBAN|FBAV|Instagram|Messenger|Line|Viber|Twitter/i.test(ua);
  };
  const [showInAppWarning] = useState(isInAppBrowser());

  const {
    isTracking,
    permissionStatus,
    totalDistance,
    currentSpeed,
    maxSpeed,
    duration,
    error,
    accuracy,
    gpsSignalQuality,
    positionCount,
    wakeLockActive,
    backgroundWarning,
    lastSavedAt,
    startTracking,
    stopTracking,
    coordinates
  } = useGPSTracking();

  // Get target distance in meters
  const getTargetDistanceMeters = useCallback(() => {
    return sessionConfig.targetDistance || 500;
  }, [sessionConfig.targetDistance]);

  // Auto-start GPS tracking if coming from Training Dashboard
  useEffect(() => {
    if (sessionConfig.autoStart && !autoStartedRef.current && permissionStatus !== 'denied') {
      autoStartedRef.current = true;
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        const success = startTracking({
          mode: sessionConfig.mode || 'practice',
          targetDistance: sessionConfig.targetDistance || 500,
          autoStopEnabled: sessionConfig.autoStopEnabled || false,
          heatNumber
        });
        if (success) {
          setSessionActive(true);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionConfig.autoStart, permissionStatus, startTracking]);

  // Briefly show "auto-saved ✓" pulse after each progress snapshot
  useEffect(() => {
    if (!lastSavedAt) return;
    setShowSavedPulse(true);
    const t = setTimeout(() => setShowSavedPulse(false), 2000);
    return () => clearTimeout(t);
  }, [lastSavedAt]);

  // Fetch last session on mount
  useEffect(() => {
    const fetchLastSession = async () => {
      try {
        const storedToken = localStorage.getItem('dbpro_session_token');
        const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
        const response = await axios.get(`${API}/sessions/latest`, { headers });
        if (response.data) {
          setLastSession(response.data);
        }
      } catch (err) {
        console.error('Error fetching last session:', err);
      }
    };
    fetchLastSession();
  }, []);

  // Auto-stop logic
  useEffect(() => {
    if (autoStopEnabled && sessionActive && isTracking) {
      const target = getTargetDistanceMeters();
      if (totalDistance >= target) {
        handleStopSession(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStopEnabled, sessionActive, isTracking, totalDistance, getTargetDistanceMeters]);

  const handleStartSession = () => {
    const heat = peekNextHeat(getTargetDistanceMeters());
    const success = startTracking({
      mode,
      targetDistance: getTargetDistanceMeters(),
      autoStopEnabled,
      heatNumber: heat
    });
    if (success) {
      incrementHeat(getTargetDistanceMeters());
      setHeatNumber(heat);
      setSessionActive(true);
    }
  };

  const handleStopSession = async (wasAutoStopped = false) => {
    const sessionData = stopTracking();
    setSessionActive(false);

    // Save auto-fill settings for next session (based on this completed session)
    const targetDist = getTargetDistanceMeters();
    const isStandardDistance = [200, 500, 1000, 2000].includes(targetDist);
    localStorage.setItem('dbpro_autofill', JSON.stringify({
      mode,
      targetDistance: isStandardDistance ? String(targetDist) : 'custom',
      customDistance: isStandardDistance ? '' : String(targetDist),
      autoStopEnabled
    }));

    // Prepare session data for summary page
    const summaryData = {
      totalDistance: sessionData.totalDistance,
      duration: sessionData.duration,
      averageSpeed: sessionData.averageSpeed,
      maxSpeed: sessionData.maxSpeed,
      targetDistance: getTargetDistanceMeters(),
      mode,
      coordinates: sessionData.coordinates || [],
      autoStopped: wasAutoStopped,
      heatNumber
    };

    // Save session to backend with token refresh and retry logic
    const session = {
      mode,
      target_distance: getTargetDistanceMeters(),
      distance_covered: sessionData.totalDistance,
      duration_seconds: sessionData.duration,
      average_speed_ms: sessionData.averageSpeed,
      max_speed_ms: sessionData.maxSpeed,
      auto_stop_enabled: autoStopEnabled,
      auto_stopped: wasAutoStopped,
      coordinates: sessionData.coordinates || [],
      heat_number: heatNumber,
      created_at_local: new Date().toISOString() // Store local timestamp for failed session recovery
    };

    // Unified save path: refresh token → POST → queue to dbpro_failed_sessions on failure
    const saveSuccessful = await saveSessionWithRetry(session);
    if (!saveSuccessful) {
      alert('Warning: Your session could not be saved to your training history. It is stored on this device and will sync automatically when you reconnect.');
    }

    // Navigate to Summary page with session data
    navigate('/summary', { state: summaryData });
  };

  const speed = formatSpeed(currentSpeed);
  const avgSpeed = duration > 0 ? formatSpeed(totalDistance / duration) : { ms: '0.00', kmh: '0.0' };
  const progress = Math.min((totalDistance / getTargetDistanceMeters()) * 100, 100);

  // Handle back navigation
  const handleBackToSetup = () => {
    if (sessionActive) {
      // If session is active, stop it first
      handleStopSession(false);
    }
    navigate('/dashboard');
  };

  return (
    <div 
      className="min-h-screen p-4 pb-8" 
      style={{ backgroundColor: BRAND.navy }}
      data-testid="dashboard-screen"
    >
      {/* Header with Back Button and Title */}
      <div className="mb-6">
        {/* Back Button */}
        {!sessionActive && (
          <button
            onClick={handleBackToSetup}
            className="flex items-center gap-2 mb-4 text-base"
            style={{ color: BRAND.mutedGray }}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Setup</span>
          </button>
        )}
        
        {/* Title */}
        <div className="text-center">
          <h1 
            className="text-2xl font-bold" 
            style={{ color: BRAND.red }}
            data-testid="dashboard-title"
          >
            Live Session
          </h1>
          <p style={{ color: BRAND.mutedGray }} className="text-base">
            {mode === 'race' ? 'Race Mode' : 'Practice Mode'} • {formatDistance(getTargetDistanceMeters())}
          </p>
        </div>
      </div>

      {/* In-App Browser Warning */}
      {showInAppWarning && (
        <div 
          className="rounded-lg p-3 mb-4 flex items-start gap-2 border"
          style={{ 
            backgroundColor: '#2196F320',
            borderColor: '#2196F3'
          }}
          data-testid="inapp-browser-warning"
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2196F3' }} />
          <div>
            <p className="text-sm font-medium" style={{ color: '#2196F3' }}>
              For best GPS tracking:
            </p>
            <p className="text-sm" style={{ color: BRAND.lightGray }}>
              Copy link and open in Chrome or Safari
            </p>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div 
          className="rounded-lg p-3 mb-4 flex items-center gap-2 border"
          style={{ 
            backgroundColor: `${BRAND.red}20`,
            borderColor: BRAND.red
          }}
          data-testid="error-message"
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: BRAND.red }} />
          <p className="text-base" style={{ color: BRAND.lightGray }}>{error}</p>
        </div>
      )}

      {/* Permission Warning */}
      {permissionStatus === 'denied' && (
        <div 
          className="rounded-lg p-3 mb-4 flex items-center gap-2 border"
          style={{ 
            backgroundColor: '#FFA00020',
            borderColor: '#FFA000'
          }}
          data-testid="permission-warning"
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#FFA000' }} />
          <p className="text-base" style={{ color: BRAND.lightGray }}>
            Location access denied. Please enable location in browser settings.
          </p>
        </div>
      )}

      {/* Auto-stop Status Indicator */}
      {autoStopEnabled && (
        <div 
          className="rounded-xl p-3 mb-4 flex items-center gap-3" 
          style={{ backgroundColor: BRAND.navyCard }}
          data-testid="auto-stop-indicator"
        >
          <Zap className="w-5 h-5" style={{ color: BRAND.red }} />
          <span className="text-base" style={{ color: BRAND.lightGray }}>
            Auto-stop enabled at {formatDistance(getTargetDistanceMeters())}
          </span>
        </div>
      )}

      {/* GPS Signal Status Indicator */}
      {sessionActive && (() => {
        const config = {
          searching: {
            bg: '#FFA00020', border: '#FFA000', color: '#FFA000',
            icon: <Signal className="w-5 h-5 animate-pulse" style={{ color: '#FFA000' }} />,
            label: 'Acquiring GPS signal...',
            detail: 'Waiting for satellite fix. Stay outdoors with a clear sky view.'
          },
          poor: {
            bg: '#F4433620', border: '#F44336', color: '#F44336',
            icon: <WifiOff className="w-5 h-5" style={{ color: '#F44336' }} />,
            label: 'GPS signal too weak — distance will not record',
            detail: 'Open this app in Chrome or Safari (not Messenger/LINE). Ensure location is ON.'
          },
          fair: {
            bg: '#FFA00020', border: '#FFA000', color: '#FFA000',
            icon: <Wifi className="w-5 h-5" style={{ color: '#FFA000' }} />,
            label: 'GPS signal fair',
            detail: accuracy ? `Accuracy: ±${Math.round(accuracy)}m — tracking active` : 'Tracking active'
          },
          good: {
            bg: '#4CAF5020', border: '#4CAF50', color: '#4CAF50',
            icon: <Wifi className="w-5 h-5" style={{ color: '#4CAF50' }} />,
            label: 'GPS signal strong',
            detail: accuracy ? `Accuracy: ±${Math.round(accuracy)}m` : 'Tracking active'
          }
        };
        const s = config[gpsSignalQuality] || config.searching;
        return (
          <div 
            className="rounded-lg p-3 mb-4 flex items-start gap-3 border"
            style={{ backgroundColor: s.bg, borderColor: s.border }}
            data-testid="gps-signal-indicator"
          >
            <div className="flex-shrink-0 mt-0.5">{s.icon}</div>
            <div className="min-w-0">
              <p className="text-sm font-semibold" style={{ color: s.color }}>{s.label}</p>
              <p className="text-xs mt-0.5" style={{ color: BRAND.mutedGray }}>{s.detail}</p>
            </div>
          </div>
        );
      })()}

      {/* Background Warning */}
      {backgroundWarning && (
        <div 
          className="rounded-lg p-3 mb-4 flex items-start gap-3 border"
          style={{ backgroundColor: '#FFA00020', borderColor: '#FFA000' }}
          data-testid="background-warning"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#FFA000' }} />
          <div className="min-w-0">
            <p className="text-sm font-semibold" style={{ color: '#FFA000' }}>Tracking gap detected</p>
            <p className="text-xs mt-0.5" style={{ color: BRAND.mutedGray }}>{backgroundWarning}</p>
          </div>
        </div>
      )}

      {/* Screen Lock Status */}
      {sessionActive && (
        <div 
          className="rounded-lg px-3 py-2 mb-4 flex items-center gap-2"
          style={{ backgroundColor: BRAND.navyCard }}
          data-testid="wake-lock-status"
        >
          <Monitor className="w-4 h-4" style={{ color: wakeLockActive ? '#4CAF50' : BRAND.darkGray }} />
          <span className="text-xs" style={{ color: wakeLockActive ? '#4CAF50' : BRAND.darkGray }}>
            {wakeLockActive 
              ? 'Screen will stay on during this session' 
              : 'Keep screen on and app visible for best tracking'}
          </span>
        </div>
      )}

      {/* Live Metrics */}
      <div 
        className="rounded-xl p-4 mb-4" 
        style={{ backgroundColor: BRAND.navyCard }}
        data-testid="live-metrics"
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base" style={{ color: BRAND.mutedGray }}>
            Live Metrics — {getHeatLabel(heatNumber)}
          </h2>
          <span
            className="flex items-center gap-1 text-xs transition-opacity duration-500"
            style={{ color: '#4CAF50', opacity: showSavedPulse ? 1 : 0 }}
            aria-hidden={!showSavedPulse}
            data-testid="autosave-indicator"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            auto-saved
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-base mb-1">
            <span style={{ color: BRAND.mutedGray }}>Progress</span>
            <span style={{ color: BRAND.red }}>{progress.toFixed(1)}%</span>
          </div>
          <div 
            className="h-3 rounded-full overflow-hidden"
            style={{ backgroundColor: BRAND.navyLight }}
          >
            <div
              className="h-full transition-all duration-300"
              style={{ 
                width: `${progress}%`,
                background: `linear-gradient(90deg, ${BRAND.red}, ${BRAND.redHover})`
              }}
              data-testid="progress-bar"
            />
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Distance */}
          <div 
            className="rounded-lg p-4" 
            style={{ backgroundColor: BRAND.navyLight }}
            data-testid="metric-distance"
          >
            <div className="flex items-center gap-2 text-base mb-1" style={{ color: BRAND.mutedGray }}>
              <Target className="w-4 h-4" />
              <span>Distance</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: BRAND.white }}>
              {formatDistance(totalDistance)}
            </p>
            <p className="text-sm" style={{ color: BRAND.darkGray }}>
              / {formatDistance(getTargetDistanceMeters())}
            </p>
          </div>

          {/* Duration */}
          <div 
            className="rounded-lg p-4" 
            style={{ backgroundColor: BRAND.navyLight }}
            data-testid="metric-duration"
          >
            <div className="flex items-center gap-2 text-base mb-1" style={{ color: BRAND.mutedGray }}>
              <Timer className="w-4 h-4" />
              <span>Duration</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: BRAND.white }}>
              {formatDuration(duration)}
            </p>
            <p className="text-sm" style={{ color: BRAND.darkGray }}>mm:ss</p>
          </div>

          {/* Current Speed */}
          <div 
            className="rounded-lg p-4" 
            style={{ backgroundColor: BRAND.navyLight }}
            data-testid="metric-speed"
          >
            <div className="flex items-center gap-2 text-base mb-1" style={{ color: BRAND.mutedGray }}>
              <Gauge className="w-4 h-4" />
              <span>Speed</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: BRAND.red }}>
              {speed.kmh} <span className="text-base font-normal">km/h</span>
            </p>
            <p className="text-sm" style={{ color: BRAND.darkGray }}>{speed.ms} m/s</p>
          </div>

          {/* Avg Speed */}
          <div 
            className="rounded-lg p-4" 
            style={{ backgroundColor: BRAND.navyLight }}
            data-testid="metric-avg-speed"
          >
            <div className="flex items-center gap-2 text-base mb-1" style={{ color: BRAND.mutedGray }}>
              <Gauge className="w-4 h-4" />
              <span>Avg Speed</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: BRAND.white }}>
              {avgSpeed.kmh} <span className="text-base font-normal">km/h</span>
            </p>
            <p className="text-sm" style={{ color: BRAND.darkGray }}>{avgSpeed.ms} m/s</p>
          </div>
        </div>

        {/* GPS Info */}
        {accuracy && (
          <div 
            className="mt-3 flex items-center gap-2 text-sm" 
            style={{ color: gpsSignalQuality === 'good' ? '#4CAF50' : gpsSignalQuality === 'fair' ? '#FFA000' : BRAND.darkGray }}
            data-testid="gps-accuracy"
          >
            <MapPin className="w-3 h-3" />
            <span>GPS: ±{Math.round(accuracy)}m</span>
            <span>• Points: {coordinates.length}</span>
          </div>
        )}
      </div>

      {/* Start/Stop Button */}
      <div className="mb-4">
        {!sessionActive ? (
          <Button
            onClick={handleStartSession}
            disabled={permissionStatus === 'denied'}
            data-testid="start-button"
            className="w-full h-16 text-xl font-bold rounded-xl flex items-center justify-center gap-3 border-0"
            style={{ 
              backgroundColor: BRAND.red,
              color: BRAND.white
            }}
          >
            <Play className="w-6 h-6" />
            Start Session
          </Button>
        ) : (
          <Button
            onClick={() => handleStopSession(false)}
            data-testid="stop-button"
            className="w-full h-16 text-xl font-bold rounded-xl flex items-center justify-center gap-3 border-2"
            style={{ 
              backgroundColor: BRAND.navyLight,
              color: BRAND.white,
              borderColor: BRAND.red
            }}
          >
            <Square className="w-6 h-6 fill-current" />
            Stop Session
          </Button>
        )}
      </div>

      {/* Last Session Summary */}
      {lastSession && (
        <div 
          className="rounded-xl p-4" 
          style={{ backgroundColor: BRAND.navyCard }}
          data-testid="last-session-summary"
        >
          <h2 
            className="text-base mb-3 flex items-center gap-2" 
            style={{ color: BRAND.mutedGray }}
          >
            <CheckCircle2 className="w-4 h-4" style={{ color: '#4CAF50' }} />
            Last Session Summary
          </h2>
          <div className="grid grid-cols-2 gap-4 text-base">
            <div>
              <span style={{ color: BRAND.darkGray }}>Distance</span>
              <p className="font-medium" style={{ color: BRAND.white }}>
                {formatDistance(lastSession.distance_covered)}
              </p>
            </div>
            <div>
              <span style={{ color: BRAND.darkGray }}>Time</span>
              <p className="font-medium" style={{ color: BRAND.white }}>
                {formatDuration(Math.round(lastSession.duration_seconds))}
              </p>
            </div>
            <div>
              <span style={{ color: BRAND.darkGray }}>Avg Speed</span>
              <p className="font-medium" style={{ color: BRAND.white }}>
                {formatSpeed(lastSession.average_speed_ms).kmh} km/h
              </p>
            </div>
            <div>
              <span style={{ color: BRAND.darkGray }}>Mode</span>
              <p className="font-medium capitalize" style={{ color: BRAND.white }}>
                {lastSession.mode}
              </p>
            </div>
          </div>
          {lastSession.auto_stopped && (
            <div 
              className="mt-3 flex items-center gap-2 text-sm" 
              style={{ color: '#4CAF50' }}
              data-testid="auto-stop-indicator"
            >
              <Zap className="w-3 h-3" />
              <span>Auto-stopped at target distance</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardScreen_DBPRO_GPS_v2_BrandStyling;
